import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'
import { onKeyStroke } from '@vueuse/core'

type KeyboardNavigationOptions<T> = {
    items: Ref<T[]>
    searchQuery: Ref<string>
    onSelect?: (item: T) => void
}

export function useKeyboardNavigation<T>(options: KeyboardNavigationOptions<T>) {
    const { items, searchQuery, onSelect } = options
    const focusedIndex = ref<number | null>(null)

    function focusPrev() {
        const len = items.value.length
        if (len === 0) {
            return
        }

        const current = focusedIndex.value ?? 0
        focusedIndex.value = (current - 1 + len) % len
    }

    function focusNext() {
        const len = items.value.length
        if (len === 0) {
            return
        }

        const current = focusedIndex.value ?? -1
        focusedIndex.value = (current + 1) % len
    }

    // Auto-select first item only when there's a search query
    watch([items, searchQuery], () => {
        const isSearching = searchQuery.value.trim().length > 0
        focusedIndex.value = isSearching && items.value.length > 0 ? 0 : null
    }, { immediate: true })

    // Returns the currently focused item
    const focusedItem = computed(() =>
        focusedIndex.value !== null ? items.value[focusedIndex.value] : null,
    )

    // Keyboard event handlers
    onKeyStroke('ArrowUp', (e) => {
        e.preventDefault()
        focusPrev()
    })

    onKeyStroke('ArrowDown', (e) => {
        e.preventDefault()
        focusNext()
    })

    onKeyStroke('Enter', (e) => {
        if (focusedItem.value && onSelect) {
            e.preventDefault()
            onSelect(focusedItem.value)
        }
    })

    return { focusedIndex, focusedItem }
}
